import { fetchTeamGroups } from "app/actions/team-group";
import TeamGroupModel from "app/models/TeamGroupModel";
import { useActions } from "app/utils/hooks";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import CustomSelect from "app/components/inputs/CustomSelector";
import { cls } from "app/utils";
import styles from "./styles.module.scss";

type ChannelOption = {
  value: string;
  label: string;
};

interface ChannelsSelectProps {
  value?: string; // selected channel ID
  onChange?: (value: string) => void;
  disabled?: boolean;
  placeholder?: string;
  resetSignal?: any;
  classname?: string;
  excludeIds?: string[];
}

const ChannelsSelect: React.FC<ChannelsSelectProps> = ({
  value,
  onChange,
  disabled = false,
  placeholder,
  resetSignal,
  classname,
  excludeIds,
}) => {
  const { t } = useTranslation();
  const { id: teamId } = useParams<{ id: string }>();
  const channelActions = useActions({ fetchTeamGroups });

  const [channelsList, setChannelsList] = useState<TeamGroupModel[]>([]);
  const [pending, setPending] = useState(false);

  const { setValue, reset } = useForm<{ channel: string }>({
    defaultValues: { channel: value || "" },
  });

  useEffect(() => {
    if (!teamId) {
      return;
    }
    setPending(true);

    channelActions
      .fetchTeamGroups(teamId)
      .then((res: TeamGroupModel[]) => {
        const filtered = res.filter(
          (a: TeamGroupModel) => !excludeIds?.includes(a.id),
        );
        setChannelsList(filtered);
      })
      .finally(() => setPending(false));
  }, [channelActions, teamId, excludeIds]);

  useEffect(() => {
    if (value !== undefined) {
      setValue("channel", value);
    }
  }, [value, setValue]);

  // reset handler from parent
  useEffect(() => {
    if (resetSignal !== undefined) {
      reset({ channel: "" });
    }
  }, [resetSignal, reset]);

  const channelOptions = channelsList.map((channel) => ({
    value: channel.id,
    label: channel.name,
  }));

  useEffect(() => {
    if (channelOptions.length > 0 && !value) {
      const firstOption = channelOptions[0];
      onChange?.(firstOption.value);
    }
  }, [channelOptions, value, onChange]);

  return (
    <CustomSelect
      className={cls(classname, styles.container)}
      isDisabled={disabled || pending}
      options={channelOptions}
      value={
        value
          ? channelOptions.find((option) => option.value === value)
          : channelOptions[0] || null
      }
      placeholder={placeholder || t("Select Channel")}
      onChange={(selectedOption: ChannelOption | null) => {
        const selectedValue = selectedOption?.value || "";
        onChange?.(selectedValue);
      }}
    />
  );
};

export default ChannelsSelect;
